"use client";

import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_DISCUSSION_DRAWER_MODE,
  DISCUSSION_DRAWER_MODES,
  DISCUSSION_DRAWER_MODE_EVENT,
  DISCUSSION_DRAWER_MODE_STORAGE_KEY,
  normalizeDiscussionDrawerMode,
} from "@/lib/discussionDrawerModes";
import { FEATURE_FLAG_NAMES, isFeatureEnabled } from "@/lib/featureFlags";

const drawerToggleEnabled = isFeatureEnabled(FEATURE_FLAG_NAMES.DISCUSSION_DRAWER_TOGGLE);

function readStoredMode() {
  if (typeof window === "undefined") return DEFAULT_DISCUSSION_DRAWER_MODE;
  if (!drawerToggleEnabled) return DISCUSSION_DRAWER_MODES.CLASSIC;
  try {
    return normalizeDiscussionDrawerMode(
      window.localStorage.getItem(DISCUSSION_DRAWER_MODE_STORAGE_KEY)
    );
  } catch {
    return DEFAULT_DISCUSSION_DRAWER_MODE;
  }
}

function writeStoredMode(nextMode) {
  if (typeof window === "undefined") return;
  if (!drawerToggleEnabled) return;
  try {
    window.localStorage.setItem(DISCUSSION_DRAWER_MODE_STORAGE_KEY, nextMode);
  } catch {
    // Local storage can be unavailable in privacy-restricted contexts.
  }
}

function dispatchModeChange(nextMode) {
  if (typeof window === "undefined") return;
  if (!drawerToggleEnabled) return;
  window.dispatchEvent(
    new CustomEvent(DISCUSSION_DRAWER_MODE_EVENT, {
      detail: { mode: nextMode },
    })
  );
}

export default function useDiscussionDrawerMode() {
  const [mode, setModeState] = useState(DEFAULT_DISCUSSION_DRAWER_MODE);

  useEffect(() => {
    setModeState(readStoredMode());

    const onModeChange = (event) => {
      setModeState(normalizeDiscussionDrawerMode(event?.detail?.mode));
    };

    window.addEventListener(DISCUSSION_DRAWER_MODE_EVENT, onModeChange);
    return () => {
      window.removeEventListener(DISCUSSION_DRAWER_MODE_EVENT, onModeChange);
    };
  }, []);

  const setMode = useCallback((nextValue) => {
    if (!drawerToggleEnabled) {
      setModeState(DISCUSSION_DRAWER_MODES.CLASSIC);
      return;
    }
    const nextMode = normalizeDiscussionDrawerMode(nextValue);
    setModeState(nextMode);
    writeStoredMode(nextMode);
    dispatchModeChange(nextMode);
  }, []);

  return { mode, setMode, drawerToggleEnabled };
}
